import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { listLeads } from '../../api/leads';
import { useBranchFilter } from '../../context/branchContext';
import { EmptyState } from '../../components/EmptyState';

/** Lead Manager dashboard's "Pending Lead Approvals" section (section 11)
 * — reuses the same GET /leads listing every other lead screen calls,
 * filtered to PENDING_APPROVAL, rather than a parallel data source. */
interface PendingApprovalsCardProps {
  title?: string;
}

export const PendingApprovalsCard = ({ title = 'Pending Lead Approvals' }: PendingApprovalsCardProps = {}) => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const query = useQuery({
    queryKey: ['dashboard', 'pending-approvals', selectedBranchId],
    queryFn: () => listLeads({ page: 1, pageSize: 5, approvalStatus: 'PENDING_APPROVAL', branchId: selectedBranchId }),
  });

  const leads = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1">{title}</Typography>
          {total > 0 && <Chip size="small" color="warning" label={total} />}
        </Stack>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate('/lead-approvals')}
        >
          View all
        </Typography>
      </Stack>
      {leads.length === 0 ? (
        <EmptyState loading={query.isLoading} message="No leads pending approval" />
      ) : (
        <List dense disablePadding>
          {leads.map((lead) => (
            <ListItemButton key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)} sx={{ borderRadius: 2, px: 1 }}>
              <ListItemText
                primary={`${lead.firstName} ${lead.lastName}`}
                secondary={`${lead.leadNumber} · ${lead.assignedTo ? `${lead.assignedTo.firstName} ${lead.assignedTo.lastName}` : 'Unassigned'} · ${lead.branch?.name ?? '—'}`}
              />
              <Box>
                <Chip size="small" label="Pending" color="warning" />
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};
